import { useQuery } from '@tanstack/react-query';

import { PendingRecurringEvent } from '@shared/types/event';

import API from '@api/API';

import { guessTimezone } from '@utilities/guessTimezone';

import { useCurrentUser } from './UserQueries';

export interface UserScheduleSubmissionDateDetail {
  _id: string;
  img?: string;
  startTime?: string;
  entertainerIds?: string[];
  promoterIds?: string[];
  showName: string;
  venue: string;
  castSize?: number;
  updatedAt: Date;
  isEntertaining: boolean;
  isProducing: boolean;
}

export interface UserScheduleSubmissionUpcomingEvent {
  _id: string;
  img?: string;
  isEntertaining: boolean;
  isProducing: boolean;
  title: string;
  startDate: string;
  startTime: string;
  venue: {
    _id: string;
    title: string;
  };
}

export interface UserScheduleSubmission {
  didSubmitThisWeek: boolean;
  weekStreakCount: number;
  dates?: string[];
  timezone?: string;
  updatedAt?: string;
  weekOf?: string;
  dateDetails?: Record<string, UserScheduleSubmissionDateDetail[] | undefined>;
  participationOptions?: {
    type: 'entertainer' | 'promoter';
    _id: string;
    title: string;
  }[];
  upcomingEvents?: UserScheduleSubmissionUpcomingEvent[];
  pendingRecurringEvents?: PendingRecurringEvent[];
}

export const useCurrentUserScheduleSubmissionQuery = () => {
  const { currentUser } = useCurrentUser();

  const timezone = guessTimezone();
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUserScheduleSubmissionQuery.queryKey(currentUser?._id ?? ''),
    queryFn: () => useCurrentUserScheduleSubmissionQuery.queryFn(timezone),
  });

  return {
    scheduleIsReady: isFetched,
    schedule: data,
    refetchSchedule: refetch,
  };
};

useCurrentUserScheduleSubmissionQuery.queryKey = (userId: string) => [
  'user',
  userId,
  'schedule-submissions',
];

useCurrentUserScheduleSubmissionQuery.queryFn = (timezone: string) =>
  API.get<UserScheduleSubmission>(
    `/v1/user/schedule-submissions?timezone=${encodeURIComponent(timezone)}`,
  );
